import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import {palette} from '../../theme/palette';
import Iconify from '../../theme/iconify/iconify';

import Model from './Model';
import Prompt from './Prompt';
import Message from './Message';
import {mislecAPI} from "../../util/axios";

function NewChat({ user, updateUser, fetchChats }) {
    const [chat, setChat] = React.useState(null);
    const [model, setModel] = React.useState('Qwen');
    const [corpus, setCorpus] = React.useState(null);
    const [prompt, setPrompt] = React.useState('');
    const [typing, setTyping] = React.useState(false);

    const { t } = useTranslation();
    const navigate = useNavigate();

    React.useEffect(() => {
        window.scrollTo(0, document.body.scrollHeight);
    }, [chat]);

    const sendMessage = () => {
        if (!user) return;
        if (!prompt) return;

        //  Timestamp
        const timestamp = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');

        //  Data preparation
        const data = {
            user: user._id,
            title: 'New chat',
            model: model,
            corpus: corpus,
            references: [],
            timestamp: timestamp,
            last_modified: timestamp,
            messages: [{
                role: 'USER',
                text: prompt,
                timestamp: timestamp
            }]
        }
        setChat(data);
        setPrompt('');
        setTyping(true);

        //  Send data to the server
        mislecAPI.post(`/api/chat`, data)
            .then((res) => {
                setTyping(false);

                //  Chat history refresh
                fetchChats();

                //  Redirect to the new chat
                navigate(`/chat/${res.data.id}`);
            })
            .catch(error => {
                setTyping(false);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    return (
        <>
            <Container
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    mb: 0,
                    mt: 10
                }}
            >
                {chat ?
                    <Container sx={{ mb: { xs: 0, md: 2 }, px: 0 }}>
                        {chat &&
                            chat.messages.map((message, index) => (
                                <Message key={index} message={message} />
                            ))
                        }
                    </Container>
                    :
                    <Container
                        sx={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            px: 0
                        }}>
                        <Grid item xs={12} sx={{ textAlign: 'center', width: { xs: '80%', md: '50%' } }}>
                            <Typography variant='h3'>{t('chat.new.title')}</Typography>
                        </Grid>
                        <Grid item xs={12} sx={{ textAlign: 'center', width: { xs: '80%', md: '50%' } }}>
                            <Typography variant='body1'>
                                {t('chat.new.start')} <b>{t('chat.new.mISLec')}</b>{t('chat.new.suffix')} {t('chat.new.end')}
                            </Typography>
                        </Grid>
                    </Container>
                }
                <Container sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'sticky',
                    bottom: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: '8px',
                    px: 0,
                    background: palette().background.default
                }}>
                    {typing && (
                        <>
                            <Grid
                                container
                                alignItems='center'
                                justifyContent='flex-start'
                                sx={{
                                    fontSize: '0.875rem',
                                    mb: 0.5,
                                    ml: 1
                                }}
                            >
                                {t('chat.message.typing')}
                                <Iconify
                                    icon='svg-spinners:3-dots-fade'
                                    style={{
                                        display: 'flex',
                                        alignSelf: 'flex-end',
                                        fontSize: '12px',
                                        height: '55%',
                                        width: 'auto',
                                        marginLeft: 1,
                                        marginBottom: 1,
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                    <Prompt
                        user={user}
                        updateUser={updateUser}
                        prompt={prompt}
                        setPrompt={setPrompt}
                        sendMessage={sendMessage}
                        chatCorpus={corpus}
                        setChatCorpus={setCorpus}
                        model={<Model updateUser={updateUser} updateModel={(model) => setModel(model)} selectedModel={model} />}
                    />
                </Container>
            </Container>
        </>
    );
}

export default NewChat;
